// Global
import React from 'react';
import { tv } from 'tailwind-variants';

// Local
import { Content, PageSpecific } from '.generated/Feature.CW.model';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { Field, LinkField, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import ButtonGroup, { ButtonGroupProps } from 'helpers/SitecoreWrappers/ButtonGroup/ButtonGroup';
import useDictionary from 'lib/hooks/useDictionary';
import classNames from 'classnames';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { getPageThemeClass } from 'lib/utils/get-page-theme-class';
import useIsEditing from 'lib/hooks/useIsEditing';
import { SvgIcon } from 'helpers/SvgIcon';
import { getPageTypeByTemplateId } from 'lib/utils/getPageTypeByTemplateId';
export type GeneralHeaderProps = Content.GeneralHeader &
  ButtonGroupProps & {
    params: {
      Theme: string;
      showPrintButton: string;
    };
  };
export type pageSpecific = PageSpecific.BasePage & {
  fields: {
    PageType?: {
      fields?: {
        Value?: Field<string>;
      };
    };
  };
};

export const HeaderButtonGroup = (props: GeneralHeaderProps): JSX.Element => {
  const { getDictionaryValue } = useDictionary();
  const isEditing = useIsEditing();
  const { title, description } = props?.fields || {};
  const { sitecoreContext } = useSitecoreContext();
  const contextWithSettings = sitecoreContext;
  const hideTranslationLink = contextWithSettings?.route?.fields
    ?.hideTranslationHeaderLink as Field<boolean>;
  const pageInfo = sitecoreContext?.route as pageSpecific;
  const templateID = contextWithSettings?.route?.templateId as string;
  const theme = getPageThemeClass(templateID);
  const { base, HeadingWrapper, Title, Description, CTAWrapper, HeaderContentWrapper } =
    TAILWIND_VARIANTS();
  const showPrintButton = props?.params?.showPrintButton
    ? props?.params?.showPrintButton === '1'
    : false;

  const isTitleEmpty = !title?.value;
  const isDescriptionEmpty = !description?.value;
  const isCta1Empty = !(props?.fields?.cta1 as LinkField)?.value?.href;
  const isCta2Empty = !(props?.fields?.cta2 as LinkField)?.value?.href;
  const isCta3Empty =
    !props?.fields?.cta3 ||
    !props?.fields?.cta3.some((item) => !!(item?.fields?.link as LinkField)?.value?.href);

  const isAllEmpty =
    isTitleEmpty && isDescriptionEmpty && isCta1Empty && isCta2Empty && isCta3Empty;

  const language = contextWithSettings?.language;
  const languages = contextWithSettings?.languages || [];
  const pagePath = contextWithSettings?.itemPath || '';

  // Check if both en and es-ES exist
  const hasEN = languages.some((lang) => lang.isoCode === 'en');
  const hasES = languages.some((lang) => lang.isoCode === 'es-ES');

  // Determine alternate language link and text
  let alternateLanguageText = '';
  const alternateLanguageHref = pagePath;
  let locale = '';

  // this logic will be changed after url setting is done
  if (hasEN && hasES) {
    if (language === 'en') {
      alternateLanguageText = getDictionaryValue('LanguageCTAText');
      locale = `es-ES`;
    } else if (language === 'es-ES') {
      alternateLanguageText = getDictionaryValue('LanguageCTAText');
      locale = `en`;
    }
  }

  // Construct a new LinkField for the language switch link
  const customLanguageLink = {
    value: {
      text: alternateLanguageText,
      href: alternateLanguageHref,
      title: alternateLanguageText,
    },
  };
  if (isAllEmpty && !isEditing) return <></>;
  return (
    <ComponentWrapper
      data-component="authorable/shared/content/generalHeader"
      params={{
        Theme: theme,
      }}
      ariaLabel="Page Header"
      className={base()}
    >
      <div className={HeaderContentWrapper()}>
        <div className={HeadingWrapper()}>
          <div className="lg:min-w-[400px] min-w-[200px]">
            {(title?.value !== '' || pageInfo?.fields?.Title?.value !== '' || isEditing) && (
              <h1 className={Title()}>
                <PlainTextWrapper field={title?.value ? title : pageInfo?.fields?.Title} />
              </h1>
            )}
            <RichTextWrapper
              className={Description()}
              field={description || pageInfo?.fields?.Description}
            />
          </div>
          {!hideTranslationLink?.value && (
            <div>
              {alternateLanguageText && locale && (
                <LinkWrapper
                  field={customLanguageLink}
                  ctaVariant="hyperlink"
                  ctaIcon="arrow-right"
                  locale={locale}
                  className="py-[11px] lg:mt-0 mt-12"
                />
              )}
            </div>
          )}
        </div>
        <div className="lg:flex items-center">
          <ButtonGroup
            {...props}
            WrapperClasses={classNames(
              CTAWrapper(),
              'lg:flex-row flex-col lg:gap-12 gap-6 flex-wrap lg:max-w-[600px]'
            )}
            ButtonWrapperClasses="lg:!gap-12 !gap-6"
          />
          {showPrintButton && (
            <div className="flex flex-col gap-[8px] lg:items-center cursor-pointer lg:mt-0 mt-[36px] lg:ml-auto">
              <div className="text-14 leading-22 font-bold font-nunitoSans text-gray-900">
                Print:
              </div>
              <button
                aria-label="Print page"
                className="w-[48px] h-[48px] bg-blue-100 rounded-full flex items-center justify-center text-blue-700"
                onClick={() => {
                  window.print();
                }}
              >
                <SvgIcon icon="print" size="lg" />
              </button>
            </div>
          )}
        </div>
      </div>
    </ComponentWrapper>
  );
};

export const HeaderJumpLink = (props: GeneralHeaderProps): JSX.Element => {
  const { title, description } = props?.fields || {};
  const { sitecoreContext } = useSitecoreContext();
  const contextWithSettings = sitecoreContext;
  const hideTranslationLink = contextWithSettings?.route?.fields
    ?.hideTranslationHeaderLink as Field<boolean>;
  const templateID = contextWithSettings?.route?.templateId as string;
  const theme = getPageThemeClass(templateID);
  const pageInfo = sitecoreContext?.route as pageSpecific;
  const isEditing = useIsEditing();
  const showPrintButton = props?.params?.showPrintButton;
  const {
    base,
    HeadingWrapper,
    Title,
    Description,
    CTAWrapper,
    JumpLinkLabel,
    HeaderContentWrapper,
  } = TAILWIND_VARIANTS();
  const { getDictionaryValue } = useDictionary();
  //check getting any of the CTA value
  const cta1HasHref = !!(props?.fields?.cta1 as LinkField)?.value?.href;
  const cta2HasHref = !!(props?.fields?.cta2 as LinkField)?.value?.href;

  const hasCta3Links = props?.fields?.cta3?.some(
    (cta3) => !!(cta3?.fields?.link as LinkField)?.value?.href
  );

  const shouldRenderWrapper = cta1HasHref || cta2HasHref || hasCta3Links;

  const isTitleEmpty = !title?.value;
  const isDescriptionEmpty = !description?.value;
  const isCta1Empty = !(props?.fields?.cta1 as LinkField)?.value?.href;
  const isCta2Empty = !(props?.fields?.cta2 as LinkField)?.value?.href;
  const isCta3Empty =
    !props?.fields?.cta3 ||
    !props?.fields?.cta3.some((item) => !!(item?.fields?.link as LinkField)?.value?.href);

  const isAllEmpty =
    isTitleEmpty && isDescriptionEmpty && isCta1Empty && isCta2Empty && isCta3Empty;

  const language = contextWithSettings?.language;
  const languages = contextWithSettings?.languages || [];
  const pagePath = contextWithSettings?.itemPath || '';

  // Check if both en and es-ES exist
  const hasEN = languages.some((lang) => lang.isoCode === 'en');
  const hasES = languages.some((lang) => lang.isoCode === 'es-ES');

  // Determine alternate language link and text
  let alternateLanguageText = '';
  const alternateLanguageHref = pagePath;
  let locale = '';

  // this logic will be changed after url setting is done
  if (hasEN && hasES) {
    if (language === 'en') {
      alternateLanguageText = getDictionaryValue('LanguageCTAText');
      locale = `es-ES`;
    } else if (language === 'es-ES') {
      alternateLanguageText = getDictionaryValue('LanguageCTAText');
      locale = `en`;
    }
  }

  // Construct a new LinkField for the language switch link
  const customLanguageLink = {
    value: {
      text: alternateLanguageText,
      href: alternateLanguageHref,
      title: alternateLanguageText,
    },
  };
  if (isAllEmpty && !isEditing) return <></>;
  return (
    <ComponentWrapper
      data-component="authorable/shared/content/generalHeader"
      aria-label="Page Header"
      params={{
        Theme: theme,
      }}
      ariaLabel="Page Header"
      className={base()}
    >
      <div className={HeaderContentWrapper()}>
        <div className={HeadingWrapper()}>
          <div className="lg:min-w-[400px] min-w-[200px]">
            {(title?.value !== '' || pageInfo?.fields?.Title?.value !== '' || isEditing) && (
              <h1 className={Title()}>
                <PlainTextWrapper field={title?.value ? title : pageInfo?.fields?.Title} />
              </h1>
            )}
            <RichTextWrapper
              className={Description()}
              field={description || pageInfo?.fields?.Description}
            />
          </div>
          {!hideTranslationLink?.value && (
            <div>
              {alternateLanguageText && locale && (
                <LinkWrapper
                  field={customLanguageLink}
                  ctaVariant="hyperlink"
                  ctaIcon="arrow-right"
                  locale={locale}
                  className="py-[11px] lg:mt-0 mt-12"
                />
              )}
            </div>
          )}
        </div>
        {shouldRenderWrapper && (
          <div className="pt-6 lg:pb-0 pb-6 lg:mt-[40px] mt-12">
            <span className={JumpLinkLabel()}>
              <PlainTextWrapper field={{ value: getDictionaryValue('JumpTo') }} />
            </span>
          </div>
        )}
        <div className="lg:flex items-center">
          <ButtonGroup
            {...props}
            WrapperClasses={classNames(CTAWrapper(), 'lg:flex-row flex-col flex-wrap gap-6 !mt-0')}
            ButtonWrapperClasses="!gap-8"
            hideCta3Icon={true}
            sidebysideCta3={true}
            isHashLink={!isCta3Empty}
          />
          {(showPrintButton === '1' ||
            getPageTypeByTemplateId(templateID) === 'teaching-sheet') && (
            <div className="flex flex-col gap-[8px] lg:items-center cursor-pointer lg:mt-0 mt-[36px] lg:ml-auto">
              <div className="text-14 leading-22 font-bold font-nunitoSans text-gray-900">
                Print:
              </div>
              <button
                aria-label="Print page"
                className="w-[48px] h-[48px] bg-blue-100 rounded-full flex items-center justify-center text-blue-700"
                onClick={() => {
                  window.print();
                }}
              >
                <SvgIcon icon="print" size="lg" />
              </button>
            </div>
          )}
        </div>
      </div>
    </ComponentWrapper>
  );
};

export const GeneralHeader = (props: GeneralHeaderProps): JSX.Element => {
  return <HeaderButtonGroup {...props} />;
};

export default GeneralHeader;

const TAILWIND_VARIANTS = tv({
  defaultVariants: {
    style: 'primary',
  },
  slots: {
    base: [
      'bg-theme-bg',
      'relative',
      'lg:pt-[64px]',
      'lg:pb-[56px]',
      'pt-[40px]',
      'pb-[32px]',
      'print:hidden',
    ],
    HeaderContentWrapper: ['max-w-screen-xxl', 'm-auto', 'lg:px-20', 'px-10'],
    HeadingWrapper: ['m-auto', 'flex', 'lg:flex-row', 'flex-col', 'lg:justify-between', 'gap-4'],
    Title: [
      'font-centuryGothic',
      'lg:text-[48px]',
      'text-[40px]',
      'font-bold',
      'lg:leading-[58px]',
      'leading-[48px]',
      'text-theme-text',
      'lg:max-w-[800px]',
    ],
    Description: [
      '[&>*]:font-centuryGothic',
      '[&>*]:text-[16px]',
      '[&>*]:font-normal',
      '[&>*]:leading-[25.6px]',
      '[&_h2]:font-centuryGothic',
      '[&_h2]:text-[16px]',
      '[&_h2]:font-normal',
      '[&_h2]:leading-[25.6px]',
      '[&_h3]:font-centuryGothic',
      '[&_h3]:text-[16px]',
      '[&_h3]:font-normal',
      '[&_h3]:leading-[25.6px]',
      'text-theme-text',
      'mt-12',
      'lg:max-w-[800px]',
    ],
    LanguageCTAWrapper: ['lg:py-0', 'py-[11px]', 'lg:mb-0', 'mb-12'],
    CTAWrapper: ['lg:py-6', 'lg:mt-[40px]', 'mt-12'],
    JumpLinkLabel: ['text-16', 'font-semibold', 'text-theme-text', 'mb-6'],
  },
  variants: {},
});
